/**
 * Avatar Component
 *
 * Displays user avatar with automatic fallback to initials.
 * - Shows profile picture if available (including Lottie animations)
 * - Shows initials in a colored circle if no picture
 * - Uses theme primary color for visual cohesion
 */

import { useMemo, useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { useThemeColors } from '@/styles';
import { logger, LogCategory } from '@/utils/logger';
import { loggedFetch, FetchTarget } from '@/utils/logged-fetch';

interface AvatarProps {
  /** Profile image URL (optional) */
  src?: string | null;
  /** User's display name for generating initials */
  displayName: string;
  /** Unique user ID for color generation */
  userId: string;
  /** Size in pixels */
  size?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Extract initials from display name
 * - "John Doe" → "JD"
 * - "Alice" → "AL"
 * - "Bob Smith Jr" → "BS"
 */
function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);

  if (parts.length === 0 || !parts[0]) {
    return '??';
  }

  if (parts.length === 1) {
    // Single name - use first 2 letters
    return parts[0].substring(0, 2).toUpperCase();
  }

  // Multiple names - use first letter of first and last name
  const firstInitial = parts[0][0];
  const lastInitial = parts[parts.length - 1][0];
  return (firstInitial + lastInitial).toUpperCase();
}

export function Avatar({
  src,
  displayName,
  userId: _userId,
  size = 48,
  className = '',
}: AvatarProps) {
  const colors = useThemeColors();
  const [lottieData, setLottieData] = useState<unknown | null>(null);
  const [isLottieFile, setIsLottieFile] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Generate initials
  const initials = useMemo(() => getInitials(displayName), [displayName]);

  // Check if URL is a Lottie file and fetch the animation data
  useEffect(() => {
    if (!src) {
      setIsLottieFile(false);
      setLottieData(null);
      setHasError(false);
      return;
    }

    // Check if it's a JSON file (Lottie animation)
    const isJson = src.toLowerCase().endsWith('.json');
    setIsLottieFile(isJson);

    if (isJson) {
      // Fetch Lottie animation data
      loggedFetch(
        src,
        {},
        { category: LogCategory.NETWORK, target: FetchTarget.AVATAR }
      )
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch Lottie animation');
          }
          return response.json();
        })
        .then(data => {
          setLottieData(data);
          setHasError(false);
        })
        .catch(error => {
          logger.error('Failed to load Lottie animation:', error);
          setHasError(true);
          setLottieData(null);
        });
    } else {
      setLottieData(null);
      setHasError(false);
    }
  }, [src]);

  // If there's an error or no src, show initials
  if (!src || hasError) {
    return (
      <div
        className={`rounded-full flex-shrink-0 flex items-center justify-center font-semibold ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: colors.primary,
          color: 'white',
          fontSize: size * 0.4, // 40% of size for good proportions
        }}
        aria-label={`${displayName}'s avatar`}
        title={displayName}
      >
        {initials}
      </div>
    );
  }

  // Render Lottie animation if it's a JSON file
  if (isLottieFile && lottieData) {
    return (
      <div
        className={`rounded-full overflow-hidden flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
      >
        <Lottie
          animationData={lottieData}
          loop={true}
          autoplay={true}
          style={{ width: size, height: size }}
        />
      </div>
    );
  }

  // Show initials while loading Lottie data
  if (isLottieFile && !lottieData) {
    return (
      <div
        className={`rounded-full flex-shrink-0 flex items-center justify-center font-semibold ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: colors.primary,
          color: 'white',
          fontSize: size * 0.4,
        }}
        aria-label={`${displayName}'s avatar`}
        title={displayName}
      >
        {initials}
      </div>
    );
  }

  // Render regular image for non-Lottie files
  return (
    <div
      className={`rounded-full overflow-hidden flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <img
        src={src}
        alt={displayName}
        className="w-full h-full object-cover"
        onError={() => {
          setHasError(true);
        }}
      />
    </div>
  );
}
